import { create } from 'zustand';
import type { RomMeta } from '../storage/romRepository';
import { listRoms, romHasLocalData } from '../storage/romRepository';
import { countConflicts } from '../storage/conflictRepository';
import type { ShaderId } from '../emulator/video/shaders';
import { EMPTY_LAYOUTS, migratePortraitLayout, type DirectionalStyle, type PadLayouts } from '../ui/padLayout';
import { STORE, get, put } from '../storage/db';
import type { ClientPrincipal } from '../storage/cloudSync';
import { DEFAULT_KEYMAP, type KeyMap } from '../input/InputManager';
import { mergeKeymap } from '../input/keymap';
import {
  DEFAULT_PSX_MEMCARD_ID,
  DEFAULT_PSX_MEMCARDS,
  type PsxMemcardMeta,
} from '../storage/psxMemcard';

/** ログイン後の画面。library = Retro、settings = エミュレータ設定、my = アカウント。 */
export type View = 'home' | 'library' | 'player' | 'settings' | 'my' | 'games' | 'platform';

export interface Settings {
  volume: number;
  muted: boolean;
  /** バーチャルパッドの不透明度 */
  padOpacity: number;
  padScale: number;
  showVirtualPad: 'auto' | 'always' | 'never';
  /** 縦持ち時にパッドを画面下、横持ち時に画面端へ寄せる */
  hapticFeedback: boolean;
  /** 機種ごとに使うバックエンド ID を固定する */
  preferredBackends: Partial<Record<string, string>>;
  cloudSync: boolean;
  showFps: boolean;
  /** 相反する方向キーの同時押しを無効化 */
  blockOpposingDirections: boolean;
  /** 巻き戻し（メモリを使うので既定は off） */
  rewindEnabled: boolean;
  /** 縦方向を整数倍に丸めてドットのちらつきを抑える */
  integerScale: boolean;
  /** 表示フィルタ（CRT / スキャンライン等） */
  shader: ShaderId;
  /** バーチャルパッドの配置（向きごと）。空なら既定配置。 */
  padLayouts: PadLayouts;
  /** 方向入力の種類（十字キー / スティック / フローティングスティック） */
  padDirectional: DirectionalStyle;
  /** エミュレーションを Worker で走らせる（実験的） */
  useWorker: boolean;
  /** KeyboardEvent.code → パッドボタン */
  keymap: KeyMap;
  /** PS1 メモリーカード一覧 */
  psxMemcards: PsxMemcardMeta[];
  /** 起動時に挿すメモリーカード */
  activePsxMemcardId: string;
}

export const DEFAULT_SETTINGS: Settings = {
  volume: 0.8,
  muted: false,
  padOpacity: 0.55,
  padScale: 1,
  showVirtualPad: 'auto',
  hapticFeedback: true,
  preferredBackends: {},
  cloudSync: false,
  showFps: false,
  blockOpposingDirections: true,
  rewindEnabled: false,
  integerScale: false,
  shader: 'none',
  padLayouts: EMPTY_LAYOUTS,
  padDirectional: 'dpad',
  useWorker: false,
  keymap: { ...DEFAULT_KEYMAP },
  psxMemcards: DEFAULT_PSX_MEMCARDS.map((c) => ({ ...c })),
  activePsxMemcardId: DEFAULT_PSX_MEMCARD_ID,
};

export interface Toast {
  id: string;
  message: string;
  tone: 'info' | 'success' | 'error';
}

interface AppState {
  view: View;
  roms: RomMeta[];
  romsLoaded: boolean;
  activeRomId: string | null;
  /** play のたびに増える。キャンセル後の再起動で PlayerScreen を必ず作り直す。 */
  playGeneration: number;
  settings: Settings;
  toasts: Toast[];
  /** 未解決の同期競合の件数。0 より大きいと My にバッジが出る。 */
  conflictCount: number;
  /** null = 未ログイン。起動時は sessionReady が false の間は判定中。 */
  sessionUser: ClientPrincipal | null;
  sessionReady: boolean;

  setView: (v: View) => void;
  setSessionUser: (user: ClientPrincipal | null) => void;
  setSessionReady: (ready: boolean) => void;
  refreshRoms: () => Promise<void>;
  refreshConflicts: () => Promise<void>;
  play: (romId: string) => void;
  exitPlayer: () => void;
  updateSettings: (patch: Partial<Settings>) => Promise<void>;
  toast: (message: string, tone?: Toast['tone']) => void;
  dismissToast: (id: string) => void;
}

const SETTINGS_KEY = 'settings';

export const useStore = create<AppState>((set, getState) => ({
  view: 'home',
  roms: [],
  romsLoaded: false,
  activeRomId: null,
  playGeneration: 0,
  settings: DEFAULT_SETTINGS,
  toasts: [],
  conflictCount: 0,
  sessionUser: null,
  sessionReady: false,

  setView: (view) => set({ view }),
  setSessionUser: (sessionUser) => set({ sessionUser }),
  setSessionReady: (sessionReady) => set({ sessionReady }),

  refreshRoms: async () => {
    const roms = await listRoms();
    set({ roms, romsLoaded: true });
  },

  refreshConflicts: async () => {
    try {
      set({ conflictCount: await countConflicts() });
    } catch {
      // 競合件数が取れなくてもプレイには影響しない
    }
  },

  play: (romId) => {
    const rom = getState().roms.find((r) => r.id === romId);
    if (rom && !romHasLocalData(rom)) {
      getState().toast(
        `「${rom.title}」の ROM をこの端末に追加してください（同じファイルをドラッグ&ドロップ）`,
        'error',
      );
      return;
    }
    set((s) => ({ activeRomId: romId, view: 'player', playGeneration: s.playGeneration + 1 }));
  },

  exitPlayer: () => {
    set({ activeRomId: null, view: 'library' });
    void getState().refreshRoms();
  },

  updateSettings: async (patch) => {
    const settings = { ...getState().settings, ...patch };
    set({ settings });
    await put(STORE.settings, { key: SETTINGS_KEY, value: settings });
  },

  toast: (message, tone = 'info') => {
    const id = crypto.randomUUID();
    set((s) => ({ toasts: [...s.toasts, { id, message, tone }] }));
    // サーバー取り込みなどは読んで消す余裕が要る。error はさらに長く。
    const ms = tone === 'error' ? 7000 : tone === 'success' ? 4500 : 3000;
    window.setTimeout(() => getState().dismissToast(id), ms);
  },

  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** 起動時に永続化された設定を読み込む。 */
export async function hydrateSettings(): Promise<void> {
  try {
    const row = await get<{ key: string; value: Settings }>(STORE.settings, SETTINGS_KEY);
    if (row?.value) {
      const merged = { ...DEFAULT_SETTINGS, ...row.value };
      merged.keymap = mergeKeymap(row.value.keymap);
      if (!Array.isArray(merged.psxMemcards) || merged.psxMemcards.length === 0) {
        merged.psxMemcards = DEFAULT_PSX_MEMCARDS.map((c) => ({ ...c }));
      }
      if (!merged.activePsxMemcardId || !merged.psxMemcards.some((c) => c.id === merged.activePsxMemcardId)) {
        merged.activePsxMemcardId = merged.psxMemcards[0]?.id ?? DEFAULT_PSX_MEMCARD_ID;
      }
      const layouts = merged.padLayouts ?? EMPTY_LAYOUTS;
      merged.padLayouts = {
        portrait: migratePortraitLayout(layouts.portrait ?? {}),
        landscape: layouts.landscape ?? {},
      };
      useStore.setState({ settings: merged });
    }
  } catch (e) {
    console.warn('[store] failed to load settings', e);
  }
}

export function selectActiveRom(state: AppState): RomMeta | null {
  return state.roms.find((r) => r.id === state.activeRomId) ?? null;
}
